"use client";

import { useTranslations } from "next-intl";

// Shown the instant a post link is clicked, until the server has the article.
//
// This route is force-dynamic (it counts a view on every request), so Next
// renders it on the server before it will navigate. Without a fallback here the
// listing simply froze on screen for about three seconds — no spinner, not even
// a URL change — and read as the site having hung.
//
// The shapes mirror BlogPostBanner and BlogPostHeader so the real article lands
// on top of its own outline rather than shifting the page around.
//
// A client component only so the label below can be translated: a loading.js is
// the Suspense fallback, so it has to render synchronously and cannot await
// getTranslations(). The English string that used to sit there was read out to
// Thai screen-reader users, and being sr-only it survived every sweep that
// looked at what the page shows.
export default function Loading() {
  const t = useTranslations("blog.post");

  return (
    <div className="bg-black" aria-busy="true" aria-live="polite">
      <span className="sr-only">{t("loading")}</span>

      {/* banner */}
      <div className="h-[360px] w-full animate-pulse bg-neutral-900 sm:h-[460px] lg:h-[580px]" />

      {/* the rounded panel that overlaps it */}
      <div className="relative z-[2] -mt-[145px] rounded-t-[32px] bg-black lg:mx-10">
        <div className="mx-auto flex max-w-[940px] flex-col items-center gap-5 px-6 pt-12 md:pt-16">
          {/* tag pills */}
          <div className="flex items-center justify-center gap-3">
            <div className="h-9 w-24 animate-pulse rounded-2xl bg-neutral-800" />
            <div className="h-9 w-20 animate-pulse rounded-2xl bg-neutral-800" />
          </div>

          {/* title, two lines */}
          <div className="h-10 w-full max-w-[820px] animate-pulse rounded-xl bg-neutral-800" />
          <div className="h-10 w-3/5 animate-pulse rounded-xl bg-neutral-800" />

          {/* date + views */}
          <div className="h-5 w-56 animate-pulse rounded-lg bg-neutral-800" />
        </div>

        {/* body copy */}
        <div className="mx-auto mt-12 flex max-w-[940px] flex-col gap-4 px-6 pb-24">
          {["w-full", "w-11/12", "w-full", "w-4/5", "w-full", "w-2/3"].map(
            (width, i) => (
              <div
                key={i}
                className={`h-4 animate-pulse rounded bg-neutral-800 ${width}`}
              />
            ),
          )}
          <div className="mt-6 h-64 w-full animate-pulse rounded-2xl bg-neutral-900" />
        </div>
      </div>
    </div>
  );
}
